export type ChecklistItemState = 'DONE' | 'IN_PROGRESS' | 'OPEN' | 'LOCKED' | 'WAIVED' | 'NOT_NEEDED';
export type ChecklistVia = 'ONLINE' | 'OFFICE' | 'PAPER' | 'IMPORTED';

const STATE_LABELS: Record<string, string> = {
  DONE: 'Done',
  IN_PROGRESS: 'In progress',
  OPEN: 'To do',
  LOCKED: 'Locked',
  WAIVED: 'Waived',
  NOT_NEEDED: 'Not needed',
};

const VIA_LABELS: Record<string, string> = {
  ONLINE: 'Online',
  OFFICE: 'By the office',
  PAPER: 'On paper',
  IMPORTED: 'Imported',
};

/** An updated tuition agreement the office kept as it is reads so, not Waived: the signed agreement stands. */
export const KEPT_AS_IT_IS = 'Kept as it is';

export function itemStateLabel(state: string | null | undefined, keptAsItIs = false): string {
  if (state === 'WAIVED' && keptAsItIs) return KEPT_AS_IT_IS;
  return (state && STATE_LABELS[state]) || 'Not recorded';
}

export function completedViaLabel(via: string | null | undefined): string {
  return (via && VIA_LABELS[via]) || 'Not recorded';
}

export function progressLabel(
  checklist: { completedAt?: string | null; progress: { done: number; total: number; waitingOn?: string[] } } | null | undefined
): string {
  if (!checklist) return '—';
  if (checklist.completedAt) {
    // Marked enrolled by the office with required steps still open.
    const open = checklist.progress.waitingOn?.length ?? 0;
    return open > 0 ? `Enrolled · ${open} still to do` : 'Done';
  }
  return `${checklist.progress.done} of ${checklist.progress.total}`;
}

/**
 * Every checklist and receipt date reads in this zone, whatever zone the
 * browser is in. It stands in until schools have their own time zone; the only
 * school today is in Texas. The server formats receipt rows in the same zone
 * (Enrollment::Receipts::DISPLAY_TIME_ZONE).
 */
export const DISPLAY_TIME_ZONE = 'America/Chicago';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * "Apr 10, 2026", or "Not recorded" when the record has no date. A moment reads
 * in DISPLAY_TIME_ZONE; a date with no time (a birthday) is never shifted.
 */
export function formatChecklistDate(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const dateOnly = DATE_ONLY.test(value.trim());
  const date = new Date(dateOnly ? `${value.trim()}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: dateOnly ? 'UTC' : DISPLAY_TIME_ZONE,
  });
}

/** "Deadline Jan 15, 2027", with " · late" once the family is past it; empty when there is no deadline. */
export function deadlineLabel(checklist: { deadlineOn?: string | null; late?: boolean | null } | null | undefined): string {
  if (!checklist?.deadlineOn) return '';
  return `Deadline ${formatChecklistDate(checklist.deadlineOn)}${checklist.late ? ' · late' : ''}`;
}
